<script>
  import sha256 from "crypto-js/sha256"
  import { onMount } from "svelte"
  import { push } from "svelte-spa-router"
  import ApplicationState from "../../Stores/applicationStates"
  import notify from "../../Script/notify"
  import Particles from "../../Shared/Particles/index.svelte"
  import { parseJWT } from "../../Script/token"
  import DocChainlogo from "./docChainlogo.svelte"
  import { getRequest, postRequest } from "../../Script/apiMethods"
  import FeatureState from "../../Stores/featureState"

  let screenGradient = ""
  /**
   * @type {boolean} - to display the forgot password page or not
   */
  let forgotPassPage = false
  /**
   * @type {boolean} - to display the eye icon or eye slash icon for password input field
   */
  let showPassword = true
  /**
   * @type {String} - to bind the input value from password field
   */
  let passwordValue = ""
  /**
   * @type {String} -to bind the input value from email field
   */
  let emailValue = ""
  /**
   * @type {String} -to bind the input value from organization field
   */
  let orgValue = ""
  /**
   * @type {undefined} - to bind the input password field element
   */
  let passwordField
  /**
   * @type {boolean} - to display a loader in btn while login request or forgot password request
   */
  let btnLoader = false
  /**
   * @typedef {Object} emailNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {emailNotification}
   */
  let emailNotification = {
    close() {},
  }
  /**
   * @typedef {Object} validEmailNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {validEmailNotification}
   */
  let validEmailNotification = {
    close() {},
  }
  /**
   * @typedef {Object} passwordNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {passwordNotification}
   */
  let passwordNotification = {
    close() {},
  }
  /**
   * @typedef {Object} loginNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {loginNotification}
   */
  let loginNotification = {
    close() {},
  }
  /**
   * @type {RegExp} regex pattern for validating email address
   */
  const emailPattern = /^[A-Z0-9.!#$%&'+\/=?^_`{|}~-]+@[A-Z0-9-]+(?:\.[A-Z0-9-]+){1,}$/i

  /**
   * Function for submitting the login details
   */
  const onLogin = async () => {
    try {
      btnLoader = true
      loginNotification.close()

      // Email validation
      emailValue = emailValue.trim()
      if (!emailValue) throw "Email value can't be empty"
      const isEmailValid = emailPattern.test(emailValue)
      if (!isEmailValid) throw "Please Enter a valid Email id"

      //password validation fo empty condition
      if (!passwordValue) throw "Password can't be empty"

      const { token } = await postRequest("/api/swagger/v2/auth/login", {
        email: emailValue,
        password: passwordValue,
      })

      $ApplicationState.token = token
      $ApplicationState.tokenhmac = sha256(token).toString()

      await fetchUserDetails()

      if ($ApplicationState.pdfEnabled) await getMSignDetails()

      notify.success("Logged in Successfully")
      push("/analytics/documents")
    } catch (error) {
      const info = "PAGE: Login\nEVENT: During login Request"
      loginNotification = notify.danger(error, info)
    } finally {
      btnLoader = false
    }
  }
  /**
   * Function (which runs in onMount condition) for fetching user Details if the token present in LocalStorage
   */
  onMount(async () => {
    try {
      const grads = [
        "linear-gradient(62deg, #FBAB7E 0%, #F7CE68 100%)",
        "linear-gradient(160deg, #0093E9 0%, #80D0C7 100%)",
        "linear-gradient(90deg, #FF9A8B 0%, #FF6A88 55%, #FF99AC 100%)",
        "linear-gradient(132deg, #F4D03F 0%, #16A085 100%)",
        "linear-gradient( 83.2deg,  rgba(150,93,233,1) 10.8%, rgba(99,88,238,1) 94.3% )",
        "linear-gradient( 68.3deg,  rgba(245,177,97,1) 0.4%, rgba(236,54,110,1) 100.2% )",
        "radial-gradient( circle farthest-corner at 10% 20%,  rgba(37,145,251,0.98) 0.1%, rgba(0,7,128,1) 99.8% )",
        "linear-gradient( 99deg,  rgba(115,18,81,1) 10.6%, rgba(28,28,28,1) 118% )",
        "linear-gradient( 63.1deg,  rgba(5,23,111,1) 16.4%, rgba(24,95,240,1) 64.5% )",
        "linear-gradient(to right, rgb(55, 65, 81), rgb(17, 24, 39), rgb(0, 0, 0))",
        "linear-gradient(to right, rgb(234, 88, 12), rgb(249, 115, 22))",
        "linear-gradient(to right, rgb(29, 78, 216), rgb(30, 64, 175), rgb(17, 24, 39))",
        "black",
      ]
      let random = Math.floor(Math.random() * grads.length)
      screenGradient = grads[random]

      if ($ApplicationState.token && $ApplicationState.tokenhmac) {
        await fetchUserDetails()

        if ($ApplicationState.pdfEnabled && !$ApplicationState.msignToken) await getMSignDetails()

        push("/analytics/documents")
      }
    } catch (error) {
      console.error(error)
      const info = "PAGE: Login\nEVENT: onMount"
      notify.danger(error, info)
    }
  })

  const getMSignDetails = async () => {
    try {
      const CONFIG = {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      }

      // Get Claim and signature
      const { claim, signature } = await getRequest("/api/swagger/v2/misc/claim?system=MULTI-SIGN", CONFIG)
      if (!claim || !signature) throw "P2BCODE::ERROR_P2B_NOTIFIED"

      // Get Msign Token for multisigner
      const { token } = await postRequest("/api/pdf-signing/v2/auth/by-common", { claim, signature })
      if (!token) throw "P2BCODE::ERROR_P2B_NOTIFIED"
      $ApplicationState.msignToken = token
    } catch (error) {
      console.error(error)
      // throw error
    }
  }

  const fetchUserDetails = async () => {
    try {
      $ApplicationState.darkMode = true

      let parsedJwt = parseJWT($ApplicationState.token)

      if (Date.now() > parsedJwt.exp * 1000 || Date.now() - parsedJwt.iat * 1000 > 86400000) throw "Token expired"

      const CONFIG = {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      }

      const data = await getRequest("/api/swagger/v2/account/user", CONFIG)

      ApplicationState.update((state) => {
        state = { ...state, ...data.userData }
        return state
      })

      // // check new updates
      // const features = await getRequest("/api/app-updates/v1/app-update/fetch?limit=1", CONFIG)

      // // console.log({ features })

      // if (features.updates.length) {
      //   $FeatureState.version = features.updates[0].version
      //   if (features.count) {
      //     const latestDate = features.updates[0].created

      //     if (!$FeatureState.notifyUser) $FeatureState.notifyUser = latestDate > $FeatureState.date ? true : false

      //     $FeatureState.date = features.updates[0].created
      //   }
      // }

      // console.log($FeatureState)
      // storedDate = localStorage.getItem("P2BUpdate")
    } catch (error) {
      console.error(error)
      $ApplicationState = {}
      localStorage.clear()
      throw error
    }
  }

  const onForgot = async () => {
    try {
      btnLoader = true

      emailValue = emailValue.trim()
      if (!emailValue) throw "Email value can't be empty"

      const isEmailValid = emailPattern.test(emailValue)
      if (!isEmailValid) throw "Please enter valid Email id"

      if (!userNumber) throw "Phone number can't be Empty"
      if (!orgValue) throw "Organization name can't be Empty"

      const msg = encodeURI(`Password forget request submitted \nEMAIL: ${emailValue}\nPHONE:${userNumber}\nORGANIZATION:${orgValue}\nURL: ${window.location.origin}`)

      await getRequest(`https://analytics.print2block.in/sendmsg?msg=${msg}`)
      forgotPassPage = false
      notify.success("Our support team will get in touch with you. &#128150")
      // notify.success("Your request to reset your password has been successfully submitted")
    } catch (error) {
      console.error(error)
      loginNotification.close()
      const info = "PAGE: Login\nEVENT: On requesting a password reset"
      loginNotification = notify.danger(error, info)
    } finally {
      btnLoader = false
    }
  }

  let userNumber

  /**
   * function to check for given pattern to template name
   * @param e - to capture key event
   */
  const checkSpecialChars = async (e) => {
    try {
      // Regular expression to match allowed characters
      let allowedCharsRegex = /^\+?[0-9\s-]+$/

      if (e.type === "paste") {
        // Get the pasted text from the clipboard
        let clipboardData = e.clipboardData || window.clipboardData
        let pastedText = clipboardData.getData("text/plain")

        setTimeout(() => {
          // Check if the pasted text contains any invalid special characters
          if (!allowedCharsRegex.test(pastedText)) {
            // Remove any characters that are not allowed
            let sanitizedText = pastedText.replace(/[^0-9\s-+]/g, "").trim()
            // Set the input value to the sanitized text
            userNumber = sanitizedText
          }
        }, 500)
      }
    } catch (error) {
      console.error(error)
      const info = "PAGE: Login\nEVENT: During phone number validation on paste."
      loginNotification = notify.danger(error, info)
    }
  }
</script>

<svelte:head>
  <title>Login</title>
</svelte:head>
<div class="relative flex h-full min-h-screen w-screen overflow-hidden mobile:h-screen">
  <!-- <div class="relative min-h-full w-full select-none overflow-hidden"> -->
  <!-- <Crackers /> -->
  <Particles {screenGradient} />
  <div class="absolute inset-0 flex h-full items-center bg-transparent p-5 mobile:w-[385px] mobile:p-0">
    <div class="flex w-full flex-col justify-between gap-3 overflow-auto rounded-2xl bg-darkgray/50 p-5 mobile:h-full mobile:rounded-none mobile:p-10">
      <div class="flex w-full flex-col mobile:items-start mobile:justify-start">
        <!-- <div class="mb-10 flex items-center justify-center gap-2 mobile:items-start mobile:justify-start"> -->
        <div class="mb-12 h-[32.11px] w-[208px]">
          <DocChainlogo />
        </div>
        <!-- heading  -->
        <div class="flex items-center justify-center mobile:items-start mobile:justify-start">
          <h1 class="mb-10 text-2xl font-bold text-white">{!forgotPassPage ? "Login" : "Forgot Password"}</h1>
        </div>

        <form class="flex w-full flex-col items-center justify-center">
          <div class="w-full">
            <p class="mb-2 text-sm text-white">Email Address</p>
            <div class="group relative mb-3 w-full mobile:mb-8">
              <input placeholder="Enter Your Email" bind:value={emailValue} type="email" autocomplete="off" />
              <div class="absolute inset-y-0 left-0 flex aspect-square items-center justify-center text-white group-focus-within:text-blue-500">
                <i class="fa-solid fa-envelope" />
              </div>
            </div>
            <div class="{forgotPassPage ? 'hidden' : 'flex'} flex-col">
              <p class="mb-2 text-sm text-white">Password</p>
              <div class="group relative mb-3 w-full mobile:mb-10">
                <input placeholder="Enter Your Password" bind:this={passwordField} bind:value={passwordValue} type="password" autocomplete="off" />

                <div class="absolute inset-y-0 left-0 flex aspect-square items-center justify-center text-white focus:bg-blue-500 group-focus-within:text-blue-500">
                  <i class="fa-solid fa-lock" />
                </div>

                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                  on:click={() => {
                    showPassword = !showPassword
                    passwordField.type = !showPassword ? "text" : "password"
                  }}
                  class="absolute inset-y-0 right-0 flex aspect-square items-center justify-center text-white"
                >
                  {#if showPassword}
                    <i class="fa-solid fa-eye cursor-pointer group-focus-within:text-blue-500" />
                  {:else}
                    <i class="fa-solid fa-eye-slash cursor-pointer group-focus-within:text-blue-500" />
                  {/if}
                </div>
              </div>

              <div class="mb-3 mobile:mb-10">
                <button
                  on:click={() => {
                    forgotPassPage = true
                  }}
                  type="button"
                  class="cursor-pointer text-sm font-normal tracking-wide text-gray-400 underline underline-offset-4 hover:text-white"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            {#if !forgotPassPage}
              <button disabled={btnLoader} on:click|preventDefault={onLogin} class="ripple w-full rounded-md bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none disabled:cursor-not-allowed">
                {#if btnLoader}
                  <i class="fa-solid fa-spinner-third fa-spin" />
                {:else}
                  <i class="fa-solid fa-right-to-bracket" />
                {/if}
                &nbsp;Login
              </button>
            {:else}
              <div class="mb-3 flex flex-col gap-3 mobile:gap-8 xls:mb-10">
                <div>
                  <p class="mb-2 text-sm text-white">Phone Number</p>
                  <div class="group relative w-full">
                    <input
                      bind:value={userNumber}
                      on:keypress={(e) => {
                        if (e.code == "KeyE") {
                          e.preventDefault()
                        }
                      }}
                      on:input={(e) => {
                        e.target.setCustomValidity("")
                        if (!e.target.validity.valid) e.target.setCustomValidity("Please enter a valid phone number starting with + followed by digits only.")
                      }}
                      on:paste={checkSpecialChars}
                      required
                      pattern="^\+?[0-9]+$"
                      type="number"
                      autocomplete="off"
                      placeholder="Enter Phone Number"
                      id="telephoneNumber"
                      name="telephoneNumber"
                    />
                    <div class="absolute inset-y-0 left-0 flex aspect-square items-center justify-center text-white group-focus-within:text-blue-500">
                      <i class="fa-solid fa-phone" />
                    </div>
                  </div>
                </div>

                <div>
                  <p class="mb-2 text-sm text-white">Organization Name</p>
                  <div class="group relative w-full">
                    <input bind:value={orgValue} required type="text" autocomplete="off" placeholder="Enter Organization Name" id="Organization" name="Organization" />
                    <div class="absolute inset-y-0 left-0 flex aspect-square items-center justify-center text-white group-focus-within:text-blue-500">
                      <i class="fa-solid fa-buildings" />
                    </div>
                  </div>
                </div>
              </div>
              <!-- <div class="relative h-[146px] overflow-hidden rounded-md">
                <div class="absolute inset-0 z-10 opacity-50" style="background: {screenGradient}" />
                <p class="absolute inset-0 z-20 flex items-center py-3 px-6 text-center text-lg font-medium text-white">Once you've shared your email address and phone number, our support team will get in touch with you. &#128150</p>
              </div> -->

              <div class="flex items-center gap-3">
                <button
                  disabled={btnLoader}
                  on:click|preventDefault={() => {
                    forgotPassPage = false
                  }}
                  class="mt-3 w-full rounded-md border border-red-500 px-4 py-2 text-sm font-medium text-white hover:border-transparent hover:bg-red-600 hover:text-white focus:outline-none disabled:cursor-not-allowed xls:mt-6"
                >
                  <i class="fa-solid fa-backward" />
                  &nbsp;Back
                </button>
                <button disabled={btnLoader} on:click|preventDefault={onForgot} class="mt-3 w-full rounded-md border border-transparent bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none disabled:cursor-not-allowed xls:mt-6">
                  {#if btnLoader}
                    <i class="fa-solid fa-spinner-third fa-spin" />
                  {:else}
                    <i class="fa-solid fa-right-to-bracket" />
                  {/if}
                  &nbsp;Submit
                </button>
              </div>
            {/if}
          </div>
        </form>
      </div>
      <div class="-mb-6 hidden w-full select-none items-center justify-center mobile:flex">
        <p class="text-center text-base text-white">
          &copy; {new Date().getFullYear()}
          <a href="https://docchain.io/" target="_blank">DocChain.io</a>
          <br />
          All rights reserved.
        </p>
      </div>
    </div>
  </div>
  <div class="absolute bottom-1 flex w-full items-center justify-center mobile:hidden xls:bottom-6">
    <p class="text-center text-base text-white">
      &copy; {new Date().getFullYear()}
      <a href="https://docchain.io/" target="_blank">DocChain.io</a>
      <br />
      All rights reserved.
    </p>
  </div>
  <!-- </div> -->
</div>

<style lang="postcss">
  input {
    @apply block w-full appearance-none rounded-md border border-white bg-transparent px-3 py-2 pl-10 text-sm text-white placeholder-gray-400 caret-white outline-none  focus:border-blue-500 focus:ring-2 focus:ring-blue-500;
  }

  /* Ripple effect */
  .ripple {
    background-position: center;
    transition: background 0.8s;
  }
  .ripple:hover {
    background: #3896e3 radial-gradient(circle, transparent 1%, #47a7f5 1%) center/15000%;
  }
  .ripple:active {
    background-color: #6eb9f7;
    background-size: 100%;
    transition: background 0s;
  }

  /* Button style */
  /* button {
    border: none;
    border-radius: 2px;
    padding: 12px 18px;
    font-size: 16px;
    text-transform: uppercase;
    cursor: pointer;
    color: white;
    background-color: #2196f3;
    box-shadow: 0 0 4px #999;
    outline: none;
  } */
</style>
